/**
 * @flow
 * @author: Pavan
 */

import React, { Component } from 'react'
import DatePicker from 'react-datepicker'
import { isToday } from 'date-fns'
import Colors from '../../themes/Colors'
import { UI_FORMAT } from '../../constants/DateConstants'
import { IcChevronDown, IcCalendar } from '../../Icons'
import styles from './styles'

import {
  DateInputContainer,
  DateFieldContainer,
  LabelText,
  AbsoluteIconContainer,
  AbsoluteLeftIconContainer,
  TextInputContainer,
  StyledCustomInput,
  CalendarContainer,
  ErrorText,
  MobileLabelText
} from './styledComponents'

type Props = {
  onChangeDate: () => mixed,
  dateLabel: string,
  placeholderText: string,
  validationEnabled: boolean,
  captureBaseRef: () => void,
  isMobile?: boolean,
  validate: () => void
}
type CustomInputProps = {
  onClick: () => void,
  value: string,
  placeholderText: string,
  displayError: boolean,
  height?: string
}

const CalendarWrapperContainer = ({ className, children }) => (
  <CalendarContainer>
    <div className={className}>
      <div style={{ position: 'relative' }}>{children}</div>
    </div>
  </CalendarContainer>
)

const DateFieldCustomInput = (props: CustomInputProps) => (
  <TextInputContainer onClick={props.onClick}>
    <CalendarIcon
      strokeColor={
        props.calendarIconColor
          ? props.calendarIconColor
          : props.dateFieldFocused
            ? Colors.twilightBlueTwo
            : Colors.blueyGreyThree
      }
    />
    <StyledCustomInput
      isMobile={props.isMobile}
      value={props.value}
      placeholder={props.placeholderText}
      style={
        props.dateFieldFocused
          ? styles.focusedStyles
          : props.displayError ? styles.errorStyles : styles.defaultStyles
      }
      onFocus={props.setDateFieldFocused}
      onBlur={props.setDateFieldFocused}
      className={props.inputClassName}
      displayErrorBorder={props.displayErrorBorder}
      height={props.height}
      readOnly
    />

    <ChevronDownIcon
      strokeColor={
        props.calendarIconColor
          ? props.calendarIconColor
          : Colors.blueyGreyThree
      }
      strokeWidth={props.arrowStrokeWidth ? props.arrowStrokeWidth : '1.5'}
      rightMargin={props.arrowRightMargin}
    />
  </TextInputContainer>
)

const ChevronDownIcon = props => (
  <AbsoluteIconContainer rightMargin={props.rightMargin}>
    <IcChevronDown {...props} />
  </AbsoluteIconContainer>
)
const CalendarIcon = props => (
  <AbsoluteLeftIconContainer>
    <IcCalendar strokeColor={props.strokeColor} />
  </AbsoluteLeftIconContainer>
)

class BaseDateField extends Component<Props> {
  state = {
    errorMsg: '',
    displayError: false,
    dateValue: '',
    dateFieldFocused: false
  }
  static defaultProps = {
    validationEnabled: true,
    isMobile: false,
    onChangeDate: () => {},
    excludeDates: [],
    displayLabel: true,
    dateFormat: UI_FORMAT
  }

  updatedDate = ''

  reset = () => {
    this.setState({
      errorMsg: '',
      displayError: false,
      dateValue: '',
      dateFieldFocused: false
    })
  }
  setDateFieldFocused = () => {
    this.setState({ dateFieldFocused: !this.state.dateFieldFocused })
  }

  captureDateFieldRef = ref => {
    this.dateFieldInstance = ref
  }

  setError = error => {
    this.setState({ errorMsg: error })
  }
  setDisplayError = displayError => {
    this.setState({ displayError })
  }

  validate = response => {
    if (response.displayError) {
      this.setError(response.errorMsg)
    }
    this.setDisplayError(response.displayError)
  }

  setBaseDateRef = ref => {
    if (ref) {
      const { captureBaseRef } = this.props
      this.dateField = ref
      if (captureBaseRef) {
        captureBaseRef(ref)
      }
    }
  }
  validateDateInput = value => {
    const { validationEnabled, validate } = this.props
    let validateResponse = { displayError: false, errorMsg: '' }
    if (validationEnabled) {
      if (validate) {
        validateResponse = validate(value)
        this.validate(validateResponse)
      }
    }
    return validateResponse
  }

  onChangeInnerDate = value => {
    const { onChangeDate, showTimeSelect, minTime } = this.props
    if (showTimeSelect) {
      // Note: Logic to make time selection mandatory
      if (minTime) {
        if (this.state.dateValue === '') {
          if (value.get('hour') < minTime.get('hour')) {
            this.updatedDate = value
            this.setState({ dateValue: '', dateFieldFocused: false })
          } else {
            this.updatedDate = this.updatedDate.set('hour', value.get('hour'))
            this.setState({
              dateValue: this.updatedDate,
              dateFieldFocused: false
            })
          }
        } else {
          this.updatedDate = value
          this.setState({
            dateValue: this.updatedDate,
            dateFieldFocused: false
          })
        }
      }
    } else {
      this.setState({ dateValue: value, dateFieldFocused: false })
    }

    onChangeDate(value)
    this.validateDateInput(value)
  }

  renderDateContent = () => {
    const { dateFieldFocused, displayError } = this.state
    const {
      onChangeDate,
      dateLabel,
      placeholderText,
      isMobile,
      excludeDates,
      inputClassName,
      calendarIconColor,
      validationEnabled,
      dateFormat,
      minTime,
      maxTime,
      showTimeSelect,
      id,
      ...otherProps
    } = this.props
    return (
      <DatePicker
        ref={this.setBaseDateRef}
        customInput={
          <DateFieldCustomInput
            isMobile={isMobile}
            id={id}
            displayError={this.state.displayError}
            placeholderText={placeholderText}
            key={`${this.state.displayError}placeholderText`}
            setDateFieldFocused={this.setDateFieldFocused}
            dateFieldFocused={dateFieldFocused}
            inputClassName={inputClassName}
            calendarIconColor={calendarIconColor}
            displayErrorBorder={validationEnabled && displayError}
            {...otherProps}
          />
        }
        selected={this.state.dateValue}
        openToDate={this.updatedDate}
        onChange={this.onChangeInnerDate}
        key={dateLabel}
        dateFormat={dateFormat}
        calendarContainer={CalendarWrapperContainer}
        showTimeSelect={showTimeSelect}
        minTime={minTime}
        maxTime={maxTime}
        {...otherProps}
        excludeDates={excludeDates}
        dayClassName={() =>
          excludeDates.filter(dateValue => isToday(dateValue)).length > 0
            ? 'disableToday'
            : undefined
        }
      />
    )
  }

  renderErrorTextContent = () => {
    const { validationEnabled, id } = this.props
    const { displayError, errorMsg } = this.state
    if (!displayError || !validationEnabled) {
      return null
    }
    return <ErrorText id={`${id}-error-message`}>{errorMsg}</ErrorText>
  }
  render() {
    const { containerClassName, id } = this.props
    const { dateLabel, isMobile, displayLabel } = this.props
    const LabelTextComponent = isMobile ? MobileLabelText : LabelText
    return (
      <DateFieldContainer className={containerClassName} id={id}>
        {displayLabel && (
          <LabelTextComponent
            id="date_picker_label"
            displayError={this.state.displayError}
          >
            {dateLabel}
          </LabelTextComponent>
        )}
        <DateInputContainer>{this.renderDateContent()}</DateInputContainer>
        <span style={{ minHeight: '20px' }}>
          {this.renderErrorTextContent()}
        </span>
      </DateFieldContainer>
    )
  }
}

export default BaseDateField



// WEBPACK FOOTER //
// ./src/commons/BaseDateField/index.js